import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  LayoutDashboard, Bus, Users, Route, MapPin, 
  DollarSign, TrendingUp, Calendar, ArrowRight, Radio, Wallet
} from 'lucide-react';
import { format } from 'date-fns';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import AIInsightsPanel from '../../components/AIInsightsPanel';
import ActivityFeed from '../../components/ActivityFeed';
import { useFeatureFlag } from '../../lib/featureFlags';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, token, loading: authLoading, isOverseer } = useAuth();
  const aiEnabled = useFeatureFlag('ai_insights');
  const activityEnabled = useFeatureFlag('realtime_activity_feed');
  const fleetEnabled = useFeatureFlag('realtime_fleet_map');
  const [stats, setStats] = useState({
    buses: 0,
    routes: 0,
    stations: 0,
    users: 0,
    todayTrips: 0,
    todayRevenue: 0,
    passengerRevenue: {},
    luggageRevenue: {},
    tripsRevenue: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && (!token || !isOverseer())) {
      navigate('/login');
      toast.error('Access denied. Admin privileges required.');
      return;
    }
    if (token && isOverseer()) {
      fetchStats();
    }
  }, [token, authLoading]);

  const fetchStats = async () => {
    try {
      const [busesRes, routesRes, stationsRes, dashRes] = await Promise.all([
        axios.get(`${API_URL}/buses`),
        axios.get(`${API_URL}/routes`),
        axios.get(`${API_URL}/stations`),
        axios.get(`${API_URL}/dashboard/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      const d = dashRes.data;
      setStats({
        buses: busesRes.data.length,
        routes: routesRes.data.length,
        stations: stationsRes.data.length,
        users: 0,
        todayTrips: d.todays_trips,
        todayRevenue: d.revenue?.USD || 0,
        todayRevenueZAR: d.revenue?.ZAR || 0,
        passengerRevenue: d.passenger_revenue || {},
        luggageRevenue: d.luggage_revenue || {},
        tripsRevenue: d.trips_revenue || []
      });
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const adminCards = [
    {
      title: 'Manage Trips',
      description: 'Create and manage bus trips',
      icon: MapPin,
      color: 'bg-blue-500',
      path: '/admin/trips'
    },
    {
      title: 'Manage Buses',
      description: 'Add and configure buses',
      icon: Bus,
      color: 'bg-green-500',
      path: '/admin/buses'
    },
    {
      title: 'Manage Routes',
      description: 'Configure routes and stops',
      icon: Route,
      color: 'bg-purple-500',
      path: '/admin/routes'
    },
    {
      title: 'Manage Users',
      description: 'Staff accounts and permissions',
      icon: Users,
      color: 'bg-orange-500',
      path: '/admin/users'
    }
  ];

  if (authLoading || loading) {
    return (
      <DashboardLayout>
        <div className="animate-pulse space-y-6">
          <div className="h-32 bg-slate-200 rounded-lg" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-32 bg-slate-200 rounded-lg" />
            ))}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-900">
              Admin Dashboard
            </h1>
            <p className="text-slate-600">
              Manage Panashe Coaches operations
            </p>
          </div>
          <Badge className="bg-panashe-gold text-slate-900 self-start">
            <LayoutDashboard className="h-3 w-3 mr-1" />
            Overseer Access
          </Badge>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="card-panashe">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Active Buses</p>
                  <p className="font-heading text-3xl font-bold text-slate-900">
                    {stats.buses}
                  </p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <Bus className="h-6 w-6 text-green-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-panashe">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Routes</p>
                  <p className="font-heading text-3xl font-bold text-slate-900">
                    {stats.routes}
                  </p>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Route className="h-6 w-6 text-purple-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-panashe">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Stations</p>
                  <p className="font-heading text-3xl font-bold text-slate-900">
                    {stats.stations}
                  </p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                  <MapPin className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-panashe col-span-2 md:col-span-4">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center shrink-0 mt-0.5">
                  <DollarSign className="h-5 w-5 text-emerald-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-500">Today's Revenue</p>
                  <p className="font-heading text-2xl font-bold text-green-600 leading-tight">
                    ${stats.todayRevenue.toFixed(2)}
                    {stats.todayRevenueZAR > 0 && (
                      <span className="text-base font-normal text-slate-400 ml-2">+ R{stats.todayRevenueZAR.toFixed(2)}</span>
                    )}
                  </p>
                  <div className="flex flex-wrap gap-2 mt-2">
                    <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 text-xs px-2 py-0.5 rounded-md font-medium">
                      👤 Passengers: ${(stats.passengerRevenue?.USD || 0).toFixed(2)}
                      {stats.passengerRevenue?.ZAR > 0 && <span className="text-slate-400">+ R{(stats.passengerRevenue?.ZAR || 0).toFixed(2)}</span>}
                    </span>
                    <span className="inline-flex items-center gap-1 bg-purple-50 text-purple-700 text-xs px-2 py-0.5 rounded-md font-medium">
                      📦 Luggage: ${(stats.luggageRevenue?.USD || 0).toFixed(2)}
                      {stats.luggageRevenue?.ZAR > 0 && <span className="text-slate-400">+ R{(stats.luggageRevenue?.ZAR || 0).toFixed(2)}</span>}
                    </span>
                  </div>
                  {stats.tripsRevenue?.length > 0 && (
                    <div className="mt-2 border-t pt-2 space-y-1.5">
                      <p className="text-[10px] uppercase tracking-wide text-slate-400 font-semibold">Per Trip Revenue</p>
                      {stats.tripsRevenue.map((tr, i) => (
                        <div key={tr.trip_id || i} className="flex flex-col sm:flex-row sm:items-center justify-between text-xs py-1 border-b border-slate-100 last:border-0 gap-1">
                          <span className="text-slate-600 truncate sm:max-w-[50%]">{tr.origin} → {tr.destination}</span>
                          <div className="flex items-center gap-2 shrink-0 flex-wrap text-[11px] sm:text-xs">
                            <span className="text-blue-600 font-medium">👤 ${(tr.passenger_revenue?.USD || 0).toFixed(2)}</span>
                            <span className="text-purple-600 font-medium">📦 ${(tr.luggage_revenue?.USD || 0).toFixed(2)}</span>
                            <span className="font-bold text-slate-900">= ${(tr.total_revenue?.USD || 0).toFixed(2)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Enterprise Layer: AI Insights + Live Activity */}
        {(aiEnabled || activityEnabled) && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {aiEnabled && (
              <div className="lg:col-span-2">
                <AIInsightsPanel />
              </div>
            )}
            {activityEnabled && (
              <div className="lg:col-span-1">
                <ActivityFeed limit={15} />
              </div>
            )}
          </div>
        )}

        {/* Live Fleet Command CTA */}
        {fleetEnabled && (
          <Card
            className="card-panashe cursor-pointer hover:shadow-lg transition-shadow border-l-4 border-l-emerald-500"
            onClick={() => navigate('/admin/fleet-live')}
            data-testid="fleet-live-cta"
          >
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <Radio className="h-6 w-6 text-emerald-600" />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-slate-900">Live Fleet Command</h3>
                  <p className="text-sm text-slate-600">Realtime view of all buses in transit · WebSocket-powered</p>
                </div>
              </div>
              <ArrowRight className="h-5 w-5 text-slate-400" />
            </CardContent>
          </Card>
        )}

        {/* Revenue Engine CTA */}
        <Card
          className="card-panashe cursor-pointer hover:shadow-lg transition-shadow border-l-4 border-l-amber-500"
          onClick={() => navigate('/admin/revenue')}
          data-testid="revenue-engine-cta"
        >
          <CardContent className="p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                <Wallet className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <h3 className="font-heading font-bold text-slate-900">Revenue Engine</h3>
                <p className="text-sm text-slate-600">Dynamic pricing · operator wallets · smart refunds · commission splits</p>
              </div>
            </div>
            <ArrowRight className="h-5 w-5 text-slate-400" />
          </CardContent>
        </Card>

        {/* Admin Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {adminCards.map((card, idx) => (
            <Card 
              key={idx}
              className="card-panashe cursor-pointer hover:shadow-lg transition-shadow"
              onClick={() => navigate(card.path)}
              data-testid={`admin-card-${card.title.toLowerCase().replace(' ', '-')}`}
            >
              <CardContent className="p-6">
                <div className="flex items-center gap-4">
                  <div className={`w-14 h-14 ${card.color} rounded-lg flex items-center justify-center`}>
                    <card.icon className="h-7 w-7 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-heading text-lg font-bold text-slate-900">
                      {card.title}
                    </h3>
                    <p className="text-sm text-slate-600">{card.description}</p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-slate-400" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Quick Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="card-panashe">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Today's Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                  <span className="text-slate-600">Scheduled Trips</span>
                  <span className="font-bold">{stats.todayTrips}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                  <span className="text-slate-600">Total Stations</span>
                  <span className="font-bold">{stats.stations}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                  <span className="text-slate-600">Active Routes</span>
                  <span className="font-bold">{stats.routes}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-panashe bg-panashe-blue text-white">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2 text-white">
                <TrendingUp className="h-5 w-5" />
                Quick Actions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <Button 
                  variant="secondary"
                  className="w-full justify-start bg-white/10 hover:bg-white/20 text-white"
                  onClick={() => navigate('/admin/trips')}
                >
                  <MapPin className="h-4 w-4 mr-2" />
                  Create New Trip
                </Button>
                <Button 
                  variant="secondary"
                  className="w-full justify-start bg-white/10 hover:bg-white/20 text-white"
                  onClick={() => navigate('/admin/buses')}
                >
                  <Bus className="h-4 w-4 mr-2" />
                  Add New Bus
                </Button>
                <Button 
                  variant="secondary"
                  className="w-full justify-start bg-white/10 hover:bg-white/20 text-white"
                  onClick={() => navigate('/admin/users')}
                >
                  <Users className="h-4 w-4 mr-2" />
                  Add Staff Member
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default AdminDashboard;
